import { jsx } from 'react/jsx-runtime';
import { useState, useRef, useEffect } from 'react';

const appendClass = (value, newClass) => {
    if (!newClass) {
        return value;
    }
    if (value) {
        return `${value} ${newClass}`;
    }
    return `${value}${newClass}`;
};
const cx = (args) => {
    let classes = '';
    const parseValue = (arg) => {
        if (typeof arg === 'string' || typeof arg === 'number') {
            return arg;
        }
        if (typeof arg !== 'object') {
            return '';
        }
        if (Array.isArray(arg)) {
            return cx(arg);
        }
        if ((arg === null || arg === void 0 ? void 0 : arg.toString) !== Object.prototype.toString &&
            !(arg === null || arg === void 0 ? void 0 : arg.toString.toString().includes('[native code]'))) {
            return arg === null || arg === void 0 ? void 0 : arg.toString();
        }
        let newclasses = '';
        // eslint-disable-next-line
        for (const key in arg) {
            // @ts-ignore
            if (Object.prototype.hasOwnProperty.call(arg, key) && arg[key]) {
                newclasses = appendClass(newclasses, key);
            }
        }
        return newclasses;
    };
    for (let i = 0; i < args.length; i++) {
        const arg = args[i];
        if (arg) {
            // @ts-ignore
            classes = appendClass(classes, parseValue(args));
        }
    }
    return classes;
};
const ANIMATION_STATE_CLASSES = {
    animating: 'animating',
    animatingUp: 'animating--up',
    animatingDown: 'animating--down',
    animatingToHeightZero: 'animating--to-height-zero',
    animatingToHeightAuto: 'animating--to-height-auto',
    animatingToHeightSpecific: 'animating--to-height-specific',
    static: 'static',
    staticHeightZero: 'static--height-zero',
    staticHeightAuto: 'static--height-auto',
    staticHeightSpecific: 'static--height-specific',
};
/**
 * Creates Animation frames
 * @param callback method to call
 * @returns Frames
 */
function startAnimationHelper(callback) {
    const requestAnimationFrameIDs = [];
    requestAnimationFrameIDs[0] = requestAnimationFrame(() => {
        requestAnimationFrameIDs[1] = requestAnimationFrame(() => {
            callback();
        });
    });
    return requestAnimationFrameIDs;
}
/**
 * Cancels Animation frames for each id provided
 * @param requestAnimationFrameIDs frame ids
 */
function cancelAnimationFrames(requestAnimationFrameIDs) {
    requestAnimationFrameIDs.forEach((id) => cancelAnimationFrame(id));
}
const getStaticStateClasses = (height) => cx({
    [ANIMATION_STATE_CLASSES.static]: true,
    [ANIMATION_STATE_CLASSES.staticHeightZero]: height === 0,
    [ANIMATION_STATE_CLASSES.staticHeightSpecific]: height > 0,
    [ANIMATION_STATE_CLASSES.staticHeightAuto]: height === 'auto',
});
const AccordionAnimation = ({ opened, height, children, }) => {
    const [currentHeight, setCurrentHeight] = useState(height);
    const [animationStateClasses, setAnimationStateClasses] = useState(() => getStaticStateClasses(height));
    const [overflow, setOverflow] = useState(null);
    const contentElement = useRef(null);
    const animationFrameIDs = useRef([]);
    const timeoutID = useRef(null);
    const animationClassesTimeoutID = useRef(null);
    const overflowTimeoutId = useRef(null);
    const contentDisplay = useRef(null);
    const showContent = () => {
        if (currentHeight === 0 && contentElement.current) {
            contentElement.current.style.display = '';
        }
    };
    const hideContent = (newHeight) => {
        if (newHeight === 0 && contentElement.current) {
            contentElement.current.style.display = 'none';
        }
    };
    useEffect(() => {
        if (opened) {
            if (contentDisplay.current) {
                clearTimeout(contentDisplay.current);
            }
            if (contentElement.current) {
                contentElement.current.style.display = '';
            }
        }
        else {
            contentDisplay.current = setTimeout(() => {
                if (contentElement.current) {
                    contentElement.current.style.display = 'none';
                }
            }, 330);
        }
        setAnimationStateClasses(getStaticStateClasses(height));
        return () => {
            cancelAnimationFrames(animationFrameIDs.current);
            clearTimeout(timeoutID.current);
            clearTimeout(animationClassesTimeoutID.current);
            clearTimeout(overflowTimeoutId.current);
            clearTimeout(contentDisplay.current);
        };
    }, []);
    useEffect(() => {
        var _a, _b, _c;
        if (opened && contentDisplay.current) {
            clearTimeout(contentDisplay.current);
            contentDisplay.current = null;
        }
        if (contentElement.current && height !== currentHeight) {
            if (contentElement.current) {
                showContent();
                contentElement.current.style.overflow = 'hidden';
            }
            const contentHeight = ((_a = contentElement.current) === null || _a === void 0 ? void 0 : _a.offsetHeight) || 0;
            if (contentElement.current) {
                contentElement.current.style.overflow = '';
            }
            let newHeight = null;
            const timeoutState = {
                height: null,
                overflow: 'hidden',
            };
            if (height === 0) {
                newHeight = height;
                timeoutState.height = newHeight;
            }
            else {
                newHeight = contentHeight;
                timeoutState.height = 'auto';
                timeoutState.overflow = null;
            }
            const isCurrentHeightAuto = currentHeight === 'auto';
            if (isCurrentHeightAuto) {
                timeoutState.height = newHeight;
                newHeight = contentHeight;
            }
            const animationClasses = cx({
                [ANIMATION_STATE_CLASSES.animating]: true,
                [ANIMATION_STATE_CLASSES.animatingUp]: currentHeight === 'auto' || height < currentHeight,
                [ANIMATION_STATE_CLASSES.animatingDown]: height === 'auto' || height > currentHeight,
                [ANIMATION_STATE_CLASSES.animatingToHeightZero]: timeoutState.height === 0,
                [ANIMATION_STATE_CLASSES.animatingToHeightAuto]: timeoutState.height === 'auto',
            });
            const timeoutAnimationStateClasses = getStaticStateClasses(timeoutState.height);
            setAnimationStateClasses(animationClasses);
            setCurrentHeight(newHeight);
            setOverflow('hidden');
            if (isCurrentHeightAuto) {
                cancelAnimationFrames(animationFrameIDs.current);
                animationFrameIDs.current = startAnimationHelper(() => {
                    setCurrentHeight(timeoutState.height);
                    setOverflow(timeoutState.overflow);
                });
                animationClassesTimeoutID.current = setTimeout(() => {
                    setAnimationStateClasses(timeoutAnimationStateClasses);
                    hideContent(timeoutState.height);
                }, 330);
            }
            else {
                timeoutID.current = setTimeout(() => {
                    setAnimationStateClasses(timeoutAnimationStateClasses);
                    setCurrentHeight(timeoutState.height);
                    setOverflow(timeoutState.overflow);
                    if (height !== 'auto') {
                        hideContent(newHeight);
                    }
                }, 330);
            }
        }
        if (opened && currentHeight === 'auto') {
            overflowTimeoutId.current = setTimeout(() => {
                setOverflow('visible');
            }, 0);
        }
        if (opened) {
            (_b = contentElement.current) === null || _b === void 0 ? void 0 : _b.removeAttribute('inert');
        }
        else {
            (_c = contentElement.current) === null || _c === void 0 ? void 0 : _c.setAttribute('inert', '');
        }
    }, [opened, height]);
    const componentStyle = {
        height: currentHeight,
        overflow: overflow,
        transition: 'height 330ms linear',
    };
    const contentStyle = Object.assign({ transition: 'opacity 330ms linear' }, (currentHeight === 0 && { opacity: 0 }));
    const componentClasses = cx({
        [animationStateClasses]: true,
        VDS__RSC__: true,
    });
    return (jsx("div", { "aria-hidden": currentHeight === 0, className: componentClasses, style: componentStyle, children: jsx("div", { style: contentStyle, ref: contentElement, children: children }) }));
};

export { AccordionAnimation as default };
